import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import GModal from "../design/components/GModal";
import Button from "../design/Button";
import { CustomInput } from "../design/components/customComponents";
import { JService } from "../api-service/ApiService";
import { getOrgURLParam } from "../../utility-functions";

const RESEND_TIMER_SECONDS = 20;

function ChangeDataRetentionPeriodModal({
  visible,
  onCancel,
  onSuccess,
  changedField,
  changedValue,
}) {
  const reduxAccount = useSelector((state) => state?.account?.value);
  const accountNumber = reduxAccount?.data?.number;
  const phoneNumber = accountNumber
    ? `${accountNumber?.country_code ?? ""} ${accountNumber?.phone_number ?? ""}`.trim()
    : "";

  //States
  const [code, setCode] = useState("");
  const [timer, setTimer] = useState(0);
  const [isConfirming, setIsConfirming] = useState(false);
  const hasSentRef = useRef(false);

  //Handlers
  const sendOtp = () => {
    setTimer(RESEND_TIMER_SECONDS);
    JService.post(`/api/v1/auth/data-retention/send-otp/`)
      .then((res) => {
        if (!res?.success)
          throw new Error(res?.message || "Could not send verification code");
        enqueueSnackbar(res?.message || "Verification code sent", {
          variant: "success",
        });
      })
      .catch((err) => {
        setTimer(0);
        enqueueSnackbar(err?.message || "Could not send verification code", {
          variant: "error",
        });
      });
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(value);
  };

  const handleConfirm = () => {
    if (code.length !== 6) return;
    setIsConfirming(true);
    JService.patch(`/api/v1/auth/data-retention/${getOrgURLParam()}`, {
      otp: code,
      [changedField]: changedValue,
    })
      .then((res) => {
        if (!res?.success)
          throw new Error(
            res?.message || "Failed to update data retention period",
          );
        enqueueSnackbar("Retention period updated successfully.", {
          variant: "success",
        });
        onSuccess?.();
      })
      .catch((err) => {
        onCancel();
        enqueueSnackbar(
          err?.message || "Failed to update data retention period",
          {
            variant: "error",
          },
        );
      })
      .finally(() => setIsConfirming(false));
  };

  //Effects
  useEffect(() => {
    if (visible && !hasSentRef.current) {
      hasSentRef.current = true;
      sendOtp();
    }
    if (!visible) {
      hasSentRef.current = false;
      setCode("");
      setTimer(0);
      setIsConfirming(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [visible]);

  const isAiInsights = changedField === "ai_insights_retention_period";

  const body = (
    <div className="change-retention-modal-body">
      <p className="description">
        {changedValue === "no_auto_deletion" ? (
          <>
            {isAiInsights ? "Transcripts and AI insights" : "Call recordings"}{" "}
            will no longer be automatically deleted.
          </>
        ) : (
          <>
            All{" "}
            {isAiInsights ? "transcripts and AI insights" : "call recordings"}{" "}
            older than <b>{changedValue} days</b> will be permanently deleted
            within <b>24 hours</b>. Call logs will still be visible, but the{" "}
            {isAiInsights ? "transcripts and AI insights" : "recordings"} will
            be removed from them. <br/> This action can't be undone.
          </>
        )}
      </p>

      <p className="verification-label">
        Please enter the verification code sent to <br/> <b>{phoneNumber}</b>
      </p>

      <CustomInput
        type="text"
        placeholder="Enter code"
        value={{ value: code }}
        handleChange={handleCodeChange}
        labelStyles={{ width: "100%", marginBottom: 0 }}
        inputContainerStyle={{ width: "100%" }}
        inputProps={{
          className: "regular-font t7 code-input",
          inputMode: "numeric",
          maxLength: 6,
          autoFocus: true,
        }}
      />

      {timer > 0 ? (
        <p className="resend-text">
          Resend code in <span className="timer">{timer}s</span>
        </p>
      ) : (
        <p className="resend-text">
          Didn't receive code?{" "}
          <button type="button" className="send-again" onClick={sendOtp}>
            Send again
          </button>
        </p>
      )}
    </div>
  );

  return (
    <GModal
      visible={visible}
      closeModal={onCancel}
      heading="Change data retention period"
      body={body}
      modalStyle={{ width: "440px" }}
      bottomButtons={[
        <Button
          buttonText="Cancel"
          hierarchy="white"
          variant="outlined"
          onClick={onCancel}
        />,
        <Button
          buttonText="Confirm change"
          hierarchy="red"
          disabled={code.length !== 6}
          isLoading={isConfirming}
          onClick={handleConfirm}
        />,
      ]}
    />
  );
}

export default ChangeDataRetentionPeriodModal;
